import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, FlaskConical, Play, History, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Files } from 'lucide-react';
import { formatDateTime, formatDisplayCode } from '@/lib/utils';

function resolveServiceModule(service) {
  if (service?.scenario === 'warranty_claim' || String(service?.service_number || '').startsWith('WAR-')) {
    return 'garantias';
  }
  return 'repairs';
}

function buildServicesUrl(serviceNumbers, module, documentKey = '') {
  const params = new URLSearchParams({ module, service_number: serviceNumbers.join(',') });
  if (documentKey) {
    params.set('qa_doc', documentKey);
  }
  return `/services?${params.toString()}`;
}

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function groupServicesByModule(services = []) {
  return services.reduce((acc, service) => {
    const module = resolveServiceModule(service);
    if (!acc[module]) acc[module] = [];
    acc[module].push(service);
    return acc;
  }, {});
}

function StatusBadge({ status }) {
  if (status === 'completed') {
    return <Badge data-testid="service-qa-run-status-completed" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Concluído</Badge>;
  }
  if (status === 'failed') {
    return <Badge data-testid="service-qa-run-status-failed" variant="destructive">Falhou</Badge>;
  }
  return <Badge data-testid="service-qa-run-status-running" variant="secondary">Em execução</Badge>;
}

function RunSummary({ run }) {
  const created = run?.created || {};
  const services = created.services || [];
  const clients = created.clients || [];
  const products = created.products || [];
  const assets = created.internal_assets || [];
  const manualCheckByService = useMemo(
    () => Object.fromEntries((run.manual_checks || []).map((check) => [check.service_number, check])),
    [run.manual_checks],
  );

  const openSingleService = (service, documentKey = '') => {
    const module = resolveServiceModule(service);
    openInNewTab(buildServicesUrl([service.service_number], module, documentKey));
  };

  const openAllServices = (documentKey = '') => {
    const grouped = groupServicesByModule(services);
    Object.entries(grouped).forEach(([module, group]) => {
      openInNewTab(buildServicesUrl(group.map((service) => service.service_number), module, documentKey));
    });
  };

  return (
    <div className="space-y-4" data-testid="service-qa-selected-run-details">
      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Serviços criados</p>
            <p className="text-2xl font-semibold" data-testid="service-qa-run-services-count">{services.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Clientes criados</p>
            <p className="text-2xl font-semibold">{clients.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Produtos/peças</p>
            <p className="text-2xl font-semibold">{products.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Activos internos</p>
            <p className="text-2xl font-semibold">{assets.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-base">Serviços gerados nesta execução</CardTitle>
              <CardDescription>Use estes números para abrir rapidamente cada cenário no ERP.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => openAllServices()} data-testid="service-qa-open-all-services-btn">
                <ExternalLink className="mr-2 h-4 w-4" />
                Abrir todos os serviços
              </Button>
              <Button variant="outline" size="sm" onClick={() => openAllServices('all-docs')} data-testid="service-qa-open-all-docs-btn">
                <Files className="mr-2 h-4 w-4" />
                Abrir todos os docs
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {services.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum serviço foi criado nesta execução.</p>
          ) : (
            services.map((service) => {
              const serviceDocs = manualCheckByService[service.service_number]?.documents || [];
              return (
                <div key={service.id} className="rounded-lg border p-3" data-testid={`service-qa-created-service-${service.service_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">{formatDisplayCode(service.service_number, resolveServiceModule(service) === 'garantias' ? 'WAR' : 'SRV')}</p>
                      <p className="text-xs text-muted-foreground">Cenário: {service.scenario}</p>
                    </div>
                    <Badge variant="outline">{service.status || 'pending'}</Badge>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground break-all">ID: {service.id}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openSingleService(service)} data-testid={`service-qa-open-service-${service.service_number}-btn`}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Abrir serviço
                    </Button>
                    {serviceDocs.map((doc) => (
                      <Button
                        key={`${service.service_number}-${doc}`}
                        variant="secondary"
                        size="sm"
                        onClick={() => openSingleService(service, doc)}
                        data-testid={`service-qa-open-doc-${service.service_number}-${doc}-btn`}
                      >
                        <Files className="mr-2 h-4 w-4" />
                        {doc}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Checklist manual sugerida</CardTitle>
          <CardDescription>Depois de gerar os dados, siga estes pontos para conferência humana.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(run.manual_checks || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem checklist adicional para esta execução.</p>
          ) : (
            (run.manual_checks || []).map((check, index) => (
              <div key={`${check.service_number}-${index}`} className="rounded-lg border p-3" data-testid={`service-qa-manual-check-${index}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{formatDisplayCode(check.service_number, check.service_number?.startsWith('WAR-') ? 'WAR' : 'SRV')}</p>
                    <p className="text-sm text-muted-foreground">{check.note}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openInNewTab(buildServicesUrl([check.service_number], resolveServiceModule({ service_number: check.service_number })))} data-testid={`service-qa-manual-open-${index}-btn`}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Abrir serviço
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(check.documents || []).map((doc) => (
                    <Button
                      key={doc}
                      variant="secondary"
                      size="sm"
                      onClick={() => openInNewTab(buildServicesUrl([check.service_number], resolveServiceModule({ service_number: check.service_number }), doc))}
                      data-testid={`service-qa-manual-doc-${index}-${doc}-btn`}
                    >
                      <Files className="mr-2 h-4 w-4" />
                      {doc}
                    </Button>
                  ))}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Passos executados</CardTitle>
          <CardDescription>Histórico detalhado da operação para replicação posterior.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(run.steps || []).map((step) => (
            <div key={`${run.id}-step-${step.index}`} className="rounded-lg border p-3 text-sm" data-testid={`service-qa-step-${step.index}`}>
              <p className="font-medium">{step.index}. {step.title}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {Object.entries(step.details || {}).map(([key, value]) => (
                  <li key={`${step.index}-${key}`} className="break-all">
                    <span className="font-medium text-foreground">{key}:</span> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export function ServiceQATab({ scenarios, runs, loading, runningScenarioKey, onRefresh, onRunScenario }) {
  const [selectedRunId, setSelectedRunId] = useState(null);

  const selectedRun = useMemo(
    () => runs.find((run) => run.id === selectedRunId) || runs[0] || null,
    [runs, selectedRunId],
  );

  const openRunServicesFromHistory = (run, documentKey = '') => {
    const grouped = groupServicesByModule(run?.created?.services || []);
    Object.entries(grouped).forEach(([module, group]) => {
      openInNewTab(buildServicesUrl(group.map((service) => service.service_number), module, documentKey));
    });
  };

  return (
    <div className="space-y-6" data-testid="service-qa-tab">
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FlaskConical className="h-5 w-5 text-primary" />
            Dados de Demonstração / QA de Serviços
          </CardTitle>
          <CardDescription>
            Gere cenários reais de Serviços e Reparações com histórico persistido. Ideal para repetir validações A/B/C sem depender de notas externas.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">Admin</Badge>
            <Badge variant="outline">Gerente de Loja</Badge>
            <Badge variant="secondary">Histórico persistido</Badge>
          </div>
          <Button variant="outline" onClick={onRefresh} data-testid="service-qa-refresh-btn" disabled={loading || Boolean(runningScenarioKey)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar histórico
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        {(scenarios || []).map((scenario) => {
          const isRunning = runningScenarioKey === scenario.key;
          return (
            <Card key={scenario.key} data-testid={`service-qa-scenario-card-${scenario.key}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{scenario.label}</CardTitle>
                    <CardDescription className="mt-1">{scenario.description}</CardDescription>
                  </div>
                  <Badge variant={scenario.key === 'full_suite' ? 'default' : 'secondary'}>
                    {scenario.key === 'full_suite' ? 'Suite completa' : 'Cenário'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Documentos a validar</p>
                  <div className="flex flex-wrap gap-2">
                    {(scenario.documents || []).map((doc) => (
                      <Badge key={doc} variant="outline">{doc}</Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                    <span>{scenario.expected_outcome}</span>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => onRunScenario(scenario.key)}
                  disabled={loading || Boolean(runningScenarioKey)}
                  data-testid={`service-qa-run-${scenario.key}-btn`}
                >
                  {isRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                  {isRunning ? 'A executar...' : scenario.key === 'full_suite' ? 'Executar suite completa' : 'Criar cenário'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Histórico de execuções QA
          </CardTitle>
          <CardDescription>As execuções ficam guardadas para consulta e repetição manual posterior.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              A carregar histórico QA...
            </div>
          ) : (runs || []).length === 0 ? (
            <div className="flex items-start gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="service-qa-empty-state">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              Ainda não existem execuções QA registadas.
            </div>
          ) : (
            <div className="space-y-2">
              {(runs || []).map((run) => (
                <div key={run.id} className="rounded-lg border p-3 transition-colors hover:bg-muted/40" data-testid={`service-qa-history-row-${run.id}`}>
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setSelectedRunId(run.id)}
                    data-testid={`service-qa-history-select-${run.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-medium">{run.scenario_label}</p>
                        <p className="text-xs text-muted-foreground">{run.location?.name} • {formatDateTime(run.created_at)}</p>
                      </div>
                      <StatusBadge status={run.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>Serviços: {run.counts?.services ?? run.created?.services?.length ?? 0}</span>
                      <span>Clientes: {run.counts?.clients ?? run.created?.clients?.length ?? 0}</span>
                      <span>Produtos: {run.counts?.products ?? run.created?.products?.length ?? 0}</span>
                      <span>Activos: {run.counts?.internal_assets ?? run.created?.internal_assets?.length ?? 0}</span>
                    </div>
                  </button>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openRunServicesFromHistory(run)} data-testid={`service-qa-history-open-services-${run.id}-btn`}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Abrir serviços criados
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openRunServicesFromHistory(run, 'all-docs')} data-testid={`service-qa-history-open-docs-${run.id}-btn`}>
                      <Files className="mr-2 h-4 w-4" />
                      Abrir docs do run
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedRun && <RunSummary run={selectedRun} />}
    </div>
  );
}
